import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';

const BRAND = {
  teal: '#4fd1c5',
  tealDark: '#2dd4bf',
  slate950: '#020617',
  slate900: '#0f172a',
  slate500: '#64748b',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

const ReviewModal = ({ open, handleClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);

  useEffect(() => {
    if (open) {
      setRating(0);
      setHover(0);
    }
  }, [open]);

  const handleSubmit = () => {
    if (rating === 0) return;
    onSubmit(rating);
  };

  const ratingLabel = (() => {
    const v = hover || rating;
    if (v === 1) return 'Pessima';
    if (v === 2) return 'Insufficiente';
    if (v === 3) return 'Discreta';
    if (v === 4) return 'Buona';
    if (v === 5) return 'Eccellente';
    return 'Tocca una stella per votare';
  })();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92vw',
          maxWidth: 440,
          bgcolor: '#fff',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(15,23,42,0.22), 0 4px 12px rgba(15,23,42,0.08)',
          fontFamily: BRAND.font,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: BRAND.slate500,
            zIndex: 2,
            '&:hover': { color: BRAND.slate900, backgroundColor: BRAND.slate100 },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.tealDark} 100%)`,
            padding: '32px 24px 24px',
            textAlign: 'center',
            color: '#fff',
          }}
        >
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.9, mb: 0.5 }}>
            Recensione viaggio
          </Typography>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Com'è andato il trasporto?
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 3, pb: 1, textAlign: 'center' }}>
          <Box display="flex" justifyContent="center" gap={0.5} mb={1}>
            {[1, 2, 3, 4, 5].map((star) => {
              const active = star <= (hover || rating);
              return (
                <IconButton
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  sx={{
                    p: 0.5,
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'scale(1.1)' },
                  }}
                >
                  {active
                    ? <StarIcon sx={{ fontSize: 36, color: '#f59e0b' }} />
                    : <StarBorderIcon sx={{ fontSize: 36, color: BRAND.slate200 }} />
                  }
                </IconButton>
              );
            })}
          </Box>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, fontWeight: 600, color: BRAND.slate500, minHeight: 22, mb: 2 }}>
            {ratingLabel}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleClose}
            sx={{
              fontFamily: BRAND.font,
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: '12px',
              borderColor: BRAND.slate200,
              color: BRAND.slate900,
              py: 1.25,
              '&:hover': { backgroundColor: BRAND.slate50, borderColor: BRAND.slate200 },
            }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={rating === 0}
            onClick={handleSubmit}
            sx={{
              fontFamily: BRAND.font,
              fontWeight: 700,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: '12px',
              py: 1.25,
              boxShadow: 'none',
              backgroundColor: BRAND.teal,
              color: '#fff',
              '&:hover': { backgroundColor: BRAND.tealDark, boxShadow: 'none' },
              '&:disabled': { backgroundColor: BRAND.slate200, color: BRAND.slate500 },
            }}
          >
            Invia recensione
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReviewModal;
